import React, { useState, useEffect } from 'react';

import {
  Box,
  Paper,
  Divider,
  TextField,
  Grid,
  Switch,
  FormControlLabel,
  FormHelperText,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  styled,
  Card,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Chip,
  List,
  ListItem,
} from '@mui/material';

import { LoadingButton } from '@mui/lab';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';

import CommonBreadcumbs from '../../common/CommonBreadcumbs';
import CommonSectionHeader from '../../common/CommonSectionHeader';
import { useSystemRoutes, routeTypes } from '../../../hooks/useSystemRoutes';
import CommonRestricted from '../../common/CommonRestricted';

import { useAppContextFeatures } from '../../../context/AppContext';
import { mergeSchema, checkValueExists } from '../../../utils/dataSchema';
import { spliceString } from '../../../utils/formatString';

import { useRegions, regionTypes } from '../../../hooks/api/useRegions';
import { useCustomFields, customFieldModules, customFieldFunctions } from '../../../hooks/api/useCustomFields';

import { useUsers, userTypes, targetTypes } from '../../../hooks/api/useUsers';
import { useSystemPrompt } from '../../../hooks/useSystemPrompt';
import {
  initialValues,
  initialPasswordValues,
  validationSchema,
  validationPasswordSchema,
} from '../../../schema/users/supplierSchema';
import { inputTypes, inputTypesDef } from '../../../schema/customFields/customFieldSchema';

import { useSystemHead, headTitles } from '../../../hooks/useSystemHead';
import { usePermissions, scopeIds, scopeTypes, functionTypes } from '../../../hooks/usePermissions';

import { useStateCustomFields } from '../../../hooks/state/useStateCustomFields';
import { useAuthAccess } from '../../../hooks/useAuthAccess';
import { useTranslations } from '../../../hooks/useTranslations';

const StyledCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
}));

export default function NewSupplier({ routeType }) {
  const { t } = useTranslations({ ns: ['common', '@user', '_common'] });

  const { promptMessage } = useSystemPrompt();
  const { setSystemLoading } = useAppContextFeatures();

  const { isLoading, create, filterOperatorTypes } = useUsers({
    userType: userTypes.supplier,
    targetType: targetTypes.supplier,
  });
  const { list: listCustomFields } = useCustomFields({
    customFieldModule: customFieldModules.user,
    customFieldFunction: customFieldFunctions.user.supplier,
  });

  const { authData } = useAuthAccess();
  const { isPermissionAllow } = usePermissions(routeType, scopeIds.user.id, scopeTypes.user.supplier.id);

  const {
    dataCustomFields,

    getInitialFormStateRows,
    getSubmitFormDataFromRows,

    getCustomFieldOptionNameByIds,

    RenderCustomFieldSelectionTextbox,
    RenderCustomFieldSelectionDropdown,
    RenderCustomFieldSelectionMultiDropdown,
  } = useStateCustomFields({
    fieldModule: customFieldModules.user,
    moduleFunction: customFieldFunctions.user.supplier,
  });

  const navigate = useNavigate();
  const { getRoute } = useSystemRoutes(routeType);

  const { list: listRegions } = useRegions({});
  const [dataCountries, setDataCountries] = useState([]);
  const [dataStates, setDataStates] = useState([]);
  const [dataAreas, setDataAreas] = useState([]);
  const { getHtmlHead } = useSystemHead(routeType);
  const [customFields, setCustomFields] = useState([]);

  const validationSchemaCombine = mergeSchema(validationSchema, validationPasswordSchema);

  const initialValuesCombine = {
    ...initialValues,
    ...initialPasswordValues,
  };

  const fetchDataCountry = async () => {
    try {
      const props = {
        limit: -1,
        offset: 0,
        filterValue: [],
        sortInfo: {
          id: 'country_name',
          dir: 0,
        },
      };

      props.regionType = regionTypes.country;

      const { data } = await listRegions(props);

      // console.log('re', data);

      setDataCountries(data?.countries);
    } catch (error) {
      promptMessage(false, `${error?.message}`);
    }
  };

  const fetchDataStates = async (countryId) => {
    setDataStates([]);

    try {
      const props = {
        limit: -1,
        offset: 0,
        filterValue: [],
        sortInfo: {
          id: 'state_name',
          dir: 0,
        },
      };

      props.regionType = regionTypes.state;
      props.countryId = countryId;

      const { data } = await listRegions(props);

      setDataStates(data?.states);
    } catch (error) {
      promptMessage(false, `${error?.message}`);
    }
  };

  const fetchDataAreas = async (stateId) => {
    setDataAreas([]);

    try {
      const props = {
        limit: -1,
        offset: 0,
        filterValue: [],
        sortInfo: {
          id: 'area_name',
          dir: 0,
        },
      };

      props.regionType = regionTypes.area;
      props.stateId = stateId;

      const { data } = await listRegions(props);

      setDataAreas(data?.areas);
    } catch (error) {
      promptMessage(false, `${error?.message}`);
    }
  };

  const onSubmit = async (currentValues) => {
    try {
      const { rows } = values;

      let cf = getSubmitFormDataFromRows({ rows });

      // for admin or retail
      const supplierType = routeType;

      let props = {
        item_id: [],
      };

      // check only if route is retail only need to set retailer id
      if (routeType === routeTypes.retail) {
        props = {
          ...props,
          retailer_id: authData?.profile?.user?.id,
        };
      }

      let data = {
        credential: {
          username: currentValues?.username,
          password: currentValues?.password,
          // user_type: currentValues?.userTypes,
          firstname: currentValues?.firstname,
          lastname: currentValues?.lastname,
          contact: currentValues?.contactno,
          email: currentValues?.email,
          status: currentValues?.status ? 1 : 0,
        },
        supplier: {
          type: supplierType,
          company_name: currentValues?.companyName,
          code: currentValues?.code,
          reg_no: currentValues?.regno,
          remark: currentValues?.remark,
          address_1: currentValues?.address1,
          address_2: currentValues?.address2,
          postcode: currentValues?.postcode,
          country_id: currentValues?.countryId,
          state_id: currentValues?.stateId,
          area: currentValues?.areaId,
          bank_name: currentValues?.bankName,
          bank_acc: currentValues?.bankAcc,
          area_id: currentValues?.areaId,
        },
        props,
        custom_fields: cf,
      };

      if (cf.length) {
        data.custom_fields = cf;
      }

      const response = await create({ data });

      promptMessage(true, `${response?.message}`);
      navigate(getRoute('users/suppliers'));
    } catch (error) {
      promptMessage(false, `Action Failed. (${error?.message})`);
    }
  };

  const loadInitialCustomFieldState = async () => {
    const cf = await getInitialFormStateRows({
      savedCustomFields: [],
    });

    setFieldValue(`rows`, cf);
  };

  const formik = useFormik({
    initialValues: initialValuesCombine,
    validationSchema: validationSchemaCombine,
    enableReinitialize: true,
    onSubmit,
  });

  const { values, errors, touched, handleChange, setFieldValue } = formik;

  useEffect(() => {
    fetchDataCountry();
    loadInitialCustomFieldState();
  }, []);

  useEffect(() => {
    if (formik?.values?.countryId) {
      if (dataStates.length) {
        if (dataStates[0].country_id !== formik.values.countryId) {
          fetchDataStates(formik.values.countryId);
        }
      } else {
        fetchDataStates(formik.values.countryId);
      }
    }

    if (formik?.values?.stateId) {
      if (dataAreas.length) {
        if (dataAreas[0].state_id !== formik.values.stateId) {
          fetchDataAreas(formik.values.stateId);
        }
      } else {
        fetchDataAreas(formik.values.stateId);
      }
    }
  }, [formik.values]);

  const checkPostcodeExists = () => {
    const checkExists = getPostcodesArray().find((i) => i === values.postcode);

    if (checkExists) return values.postcode;

    return '';
  };

  const getPostcodesArray = () => {
    let postcodeStringCheck = dataAreas.filter((i) => i.id === formik.values.areaId);

    if (postcodeStringCheck.length <= 0) return [];

    let postcodeString = postcodeStringCheck.length >= 0 ? postcodeStringCheck[0].postcode_string : '';

    let postcodeArray = postcodeString.includes(',') ? postcodeString.split(',') : [postcodeString];

    if (postcodeArray.length <= 0) return [];

    return postcodeArray;
  };

  const RenderCustomFieldSelection = () => {
    // check is empty custom fields
    if (dataCustomFields.length <= 0) return <></>;

    if (!values?.rows) return <></>;

    const rows = values.rows;

    return (
      <>
        <Grid item xs={12}>
          <Divider sx={{ mt: 2, mb: 1 }} />
        </Grid>

        <Grid item xs={12} sm={12} md={3} textAlign={'left'}>
          <CommonSectionHeader title={t('customProperties')} subTitle={t('provideCustomItemInformation')} />
        </Grid>
        <Grid item xs={12} sm={12} md={9}>
          <List>
            {rows.map((item, index) => {
              let itemValues = item;

              let { fieldType, fieldLabel, fieldKey } = item;

              return (
                <ListItem
                  key={index}
                  divider
                  disablePadding
                  sx={{
                    py: 1,
                  }}
                >
                  <Grid container spacing={1}>
                    <Grid
                      item
                      md={3}
                      xs={12}
                      sm={12}
                      display={'flex'}
                      flexDirection={'column'}
                      justifyContent={'center'}
                      alignItems={'center'}
                    >
                      <Chip
                        label={fieldLabel}
                        color="primary"
                        variant="outlined"
                        size="large"
                        // avatar={<CommonIcons name={icons.add} />}
                        sx={{
                          width: '100%',
                          // textAlign: 'left',
                          fontSize: 16,
                          fontWeight: 800,
                        }}
                      />
                    </Grid>
                    <Grid item md={9} xs={12} sm={12}>
                      {fieldType === inputTypesDef.textbox &&
                        RenderCustomFieldSelectionTextbox({
                          index,
                          item,
                          itemValues,
                          inputProps: {
                            fullWidth: true,
                            name: `rows.${index}.customValue`,
                            label: itemValues?.fieldLabel,
                            type: 'text',
                            variant: 'outlined',
                            value: itemValues?.customValue,
                            onChange: (event) => setFieldValue(`rows.${index}.customValue`, event.target.value),
                          },
                        })}

                      {fieldType === inputTypesDef.dropdown &&
                        RenderCustomFieldSelectionDropdown({
                          index,
                          item,
                          itemValues,
                          selectProps: {
                            labelId: 'customValue',
                            label: itemValues?.fieldLabel,
                            name: `rows.${index}.customArray`,
                            onChange: (event) => {
                              setFieldValue(`rows.${index}.customArray`, [event.target.value]);
                            },
                            size: 'small',
                            margin: 'dense',
                            value: itemValues.customArray.length ? itemValues.customArray[0] : '',
                          },
                        })}

                      {fieldType === inputTypesDef.multidropdown &&
                        RenderCustomFieldSelectionMultiDropdown({
                          index,
                          item,
                          itemValues,
                          selectProps: {
                            labelId: itemValues?.fieldLabel,
                            label: itemValues?.fieldLabel,
                            name: `rows.${index}.customArray`,
                            onChange: handleChange,
                            renderValue: (selected) => {
                              return (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                  {getCustomFieldOptionNameByIds(selected, dataCustomFields, fieldKey).map(
                                    (i, index) => (
                                      <Chip
                                        key={index}
                                        // color={'red'}
                                        label={spliceString({ val: i, maxLength: 20 })}
                                      />
                                    )
                                  )}
                                </Box>
                              );
                            },
                            size: 'small',
                            margin: 'dense',
                            multiple: true,
                            input: <OutlinedInput id="select-multiple-chip" label="Chip" />,
                            value: itemValues?.customArray ? itemValues.customArray : [],
                          },
                        })}
                    </Grid>
                  </Grid>
                </ListItem>
              );
            })}
          </List>
        </Grid>
      </>
    );
  };

  const RenderCredentialInfo = () => {
    return (
      <>
        <Grid item xs={12} sm={12} md={3} textAlign={'left'}>
          <CommonSectionHeader title={t('credential')} subTitle={t('accessAuthentication')} />
        </Grid>

        <Grid item xs={12} sm={12} md={9}>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={12} md={6}>
              <TextField
                margin="dense"
                size="small"
                autoFocus
                fullWidth
                name={'username'}
                label={t('username')}
                type="text"
                variant="outlined"
                value={values.username}
                onChange={handleChange}
                error={touched.username && Boolean(errors.username)}
                helperText={touched.username && errors.username}
              />
            </Grid>
            <Grid item xs={12} md={6} textAlign="left">
              <FormControlLabel
                control={
                  <Switch
                    sx={{
                      ml: 2,
                      my: 1,
                    }}
                    name="status"
                    checked={Boolean(values.status)}
                    onChange={(event, checked) => {
                      setFieldValue('status', checked);
                    }}
                  />
                }
                label={Boolean(values.status) ? t('enabled') : t('disabled')}
              />
            </Grid>

            <Grid item xs={12} sm={12} md={6}>
              <TextField
                margin="dense"
                size="small"
                fullWidth
                name={'password'}
                label={t('password')}
                type="password"
                variant="outlined"
                value={values.password}
                onChange={handleChange}
                error={touched.password && Boolean(errors.password)}
                helperText={touched.password && errors.password}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              <TextField
                margin="dense"
                size="small"
                fullWidth
                name={'passwordconfirm'}
                label={t('confirmPassword')}
                type="password"
                variant="outlined"
                value={values.passwordconfirm}
                onChange={handleChange}
                error={touched.passwordconfirm && Boolean(errors.passwordconfirm)}
                helperText={touched.passwordconfirm && errors.passwordconfirm}
              />
            </Grid>
          </Grid>
        </Grid>
      </>
    );
  };

  const RenderProfileInfo = () => {
    return (
      <>
        <Grid item xs={12} sm={12} md={3} textAlign={'left'}>
          <CommonSectionHeader title={t('supplierProfile')} subTitle={t('fullDetailsOfSupplierInformation')} />
        </Grid>

        <Grid item xs={12} sm={12} md={9}>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <TextField
                margin="dense"
                size="small"
                fullWidth
                name={'firstname'}
                label={t('firstName')}
                type="text"
                variant="outlined"
                value={values.firstname}
                onChange={handleChange}
                error={touched.firstname && Boolean(errors.firstname)}
                helperText={touched.firstname && errors.firstname}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                margin="dense"
                size="small"
                fullWidth
                name={'lastname'}
                label={t('lastName')}
                type="text"
                variant="outlined"
                value={values.lastname}
                onChange={handleChange}
                error={touched.lastname && Boolean(errors.lastname)}
                helperText={touched.lastname && errors.lastname}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                margin="dense"
                size="small"
                fullWidth
                name={'companyName'}
                label={t('companyName')}
                type="text"
                variant="outlined"
                value={values.companyName}
                onChange={handleChange}
                error={touched.companyName && Boolean(errors.companyName)}
                helperText={touched.companyName && errors.companyName}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                margin="dense"
                size="small"
                fullWidth
                name={'code'}
                label={t('code')}
                type="text"
                variant="outlined"
                value={values.code}
                onChange={handleChange}
                error={touched.code && Boolean(errors.code)}
                helperText={touched.code && errors.code}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                margin="dense"
                size="small"
                fullWidth
                name={'regno'}
                label={t('regNo')}
                type="text"
                variant="outlined"
                value={values.regno}
                onChange={handleChange}
                error={touched.regno && Boolean(errors.regno)}
                helperText={touched.regno && errors.regno}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                margin="dense"
                size="small"
                fullWidth
                name={'email'}
                label={t('email')}
                type="email"
                variant="outlined"
                value={values.email}
                onChange={handleChange}
                error={touched.email && Boolean(errors.email)}
                helperText={touched.email && errors.email}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                margin="dense"
                size="small"
                fullWidth
                name={'contactno'}
                label={t('contact')}
                type="text"
                variant="outlined"
                value={values.contactno}
                onChange={handleChange}
                error={touched.contactno && Boolean(errors.contactno)}
                helperText={touched.contactno && errors.contactno}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                margin="dense"
                size="small"
                fullWidth
                rows={3}
                multiline
                name={'remark'}
                label={t('remark')}
                type="text"
                variant="outlined"
                value={values.remark}
                onChange={handleChange}
                error={touched.remark && Boolean(errors.remark)}
                helperText={touched.remark && errors.remark}
              />
            </Grid>
          </Grid>
        </Grid>
      </>
    );
  };

  const RenderAddressInfo = () => {
    return (
      <>
        <Grid item xs={12} sm={12} md={3} textAlign={'left'}>
          <CommonSectionHeader title={t('address')} subTitle={t('fullAddressDetailsOfSupplierInformation')} />
        </Grid>

        <Grid item xs={12} sm={12} md={9}>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <TextField
                margin="dense"
                size="small"
                fullWidth
                name={'address1'}
                label={t('address1')}
                type="text"
                variant="outlined"
                value={values.address1}
                onChange={handleChange}
                error={touched.address1 && Boolean(errors.address1)}
                helperText={touched.address1 && errors.address1}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                margin="dense"
                size="small"
                fullWidth
                name={'address2'}
                label={t('address2')}
                type="text"
                variant="outlined"
                value={values.address2}
                onChange={handleChange}
                error={touched.address2 && Boolean(errors.address2)}
                helperText={touched.address2 && errors.address2}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl margin="dense" fullWidth size="small" error={touched.countryId && Boolean(errors.countryId)}>
                <InputLabel id="country">{t('country')}</InputLabel>
                <Select
                  labelId="countryId"
                  label={t('country')}
                  name={'countryId'}
                  onChange={(event) => {
                    setFieldValue(`countryId`, event.target.value);
                    setFieldValue(`stateId`, '');
                    setFieldValue(`areaId`, '');
                    setDataStates([]);
                    setDataAreas([]);
                  }}
                  value={checkValueExists({
                    dataSearch: dataCountries,
                    valueToCompare: values.countryId,
                  })}
                  sx={{
                    textAlign: 'left',
                  }}
                >
                  {dataCountries.map((i, index) => {
                    return (
                      <MenuItem key={index} value={i.id}>
                        {i.country_name}
                      </MenuItem>
                    );
                  })}
                </Select>
                <FormHelperText>{touched.countryId && errors.countryId}</FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <FormControl margin="dense" fullWidth size="small" error={touched.stateId && Boolean(errors.stateId)}>
                <InputLabel id="state">{t('state')}</InputLabel>
                <Select
                  labelId="stateId"
                  label={t('state')}
                  name={'stateId'}
                  onChange={(event) => {
                    setFieldValue(`stateId`, event.target.value);
                    setFieldValue(`areaId`, '');
                    setDataAreas([]);
                  }}
                  value={checkValueExists({
                    dataSearch: dataStates,
                    valueToCompare: values.stateId,
                  })}
                  sx={{
                    textAlign: 'left',
                  }}
                >
                  {dataStates.map((i, index) => {
                    return (
                      <MenuItem key={index} value={i.id}>
                        {i.state_name}
                      </MenuItem>
                    );
                  })}
                </Select>
                <FormHelperText>{touched.stateId && errors.stateId}</FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={12} md={6}>
              <FormControl margin="dense" fullWidth size="small" error={touched.areaId && Boolean(errors.areaId)}>
                <InputLabel id="area">{t('area')}</InputLabel>
                <Select
                  labelId="areaId"
                  label={t('area')}
                  name={'areaId'}
                  onChange={handleChange}
                  value={checkValueExists({
                    dataSearch: dataAreas,
                    valueToCompare: values.areaId,
                  })}
                  sx={{
                    textAlign: 'left',
                  }}
                >
                  {dataAreas.map((i, index) => {
                    return (
                      <MenuItem key={index} value={i.id}>
                        {i.area_name}
                      </MenuItem>
                    );
                  })}
                </Select>
                <FormHelperText>{touched.areaId && errors.areaId}</FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <FormControl margin="dense" fullWidth size="small" error={touched.postcode && Boolean(errors.postcode)}>
                <InputLabel id="postcode">{t('postcode')}</InputLabel>
                <Select
                  labelId="postcode"
                  label={t('psotcode')}
                  name={'postcode'}
                  onChange={handleChange}
                  value={checkPostcodeExists()}
                  sx={{
                    textAlign: 'left',
                  }}
                >
                  {getPostcodesArray().length ? (
                    getPostcodesArray().map((i, index) => {
                      return (
                        <MenuItem key={index} value={i}>
                          {i}
                        </MenuItem>
                      );
                    })
                  ) : (
                    <MenuItem value={''}>{null}</MenuItem>
                  )}
                </Select>
                <FormHelperText>{touched.postcode && errors.postcode}</FormHelperText>
              </FormControl>
            </Grid>
          </Grid>
        </Grid>
      </>
    );
  };

  const RenderBankInfo = () => {
    return (
      <>
        <Grid item xs={12} sm={12} md={3} textAlign={'left'}>
          <CommonSectionHeader title={t('bankInfo')} subTitle={t('supplierBankDetails')} />
        </Grid>

        <Grid item xs={12} sm={12} md={9}>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <TextField
                margin="dense"
                size="small"
                fullWidth
                name={'bankName'}
                label={t('bankName')}
                type="text"
                variant="outlined"
                value={values.bankName}
                onChange={handleChange}
                error={touched.bankName && Boolean(errors.bankName)}
                helperText={touched.bankName && errors.bankName}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                margin="dense"
                size="small"
                fullWidth
                name={'bankAcc'}
                label={t('bankAccount')}
                type="text"
                variant="outlined"
                value={values.bankAcc}
                onChange={handleChange}
                error={touched.bankAcc && Boolean(errors.bankAcc)}
                helperText={touched.bankAcc && errors.bankAcc}
              />
            </Grid>
          </Grid>
        </Grid>
      </>
    );
  };

  const RenderForm = () => {
    return (
      <form onSubmit={formik.handleSubmit}>
        {/* {JSON.stringify(authData)} */}
        <StyledCard>
          <Grid container spacing={1}>
            {RenderCredentialInfo()}

            <Grid item xs={12}>
              <Divider sx={{ mt: 1, mb: 1 }} />
            </Grid>

            {RenderProfileInfo()}

            <Grid item xs={12}>
              <Divider sx={{ mt: 1, mb: 1 }} />
            </Grid>

            {RenderAddressInfo()}

            <Grid item xs={12}>
              <Divider sx={{ mt: 1, mb: 1 }} />
            </Grid>

            {RenderBankInfo()}

            {RenderCustomFieldSelection()}
          </Grid>
        </StyledCard>

        <Grid container spacing={1} sx={{ pl: 2, pr: 2 }}>
          <Grid item md={12} textAlign={'right'}>
            <LoadingButton
              type="submit"
              // fullWidth
              variant="contained"
              sx={{
                mt: 3,
                mb: 2,
              }}
              loading={isLoading}
            >
              {t('saveChanges')}
            </LoadingButton>
          </Grid>
        </Grid>
      </form>
    );
  };

  return (
    <Box>
      {getHtmlHead({ routeType, title: headTitles.newSupplier })}

      <CommonBreadcumbs
        routeType={routeType}
        pageTitle={t('newSupplier')}
        breadcrumbs={[
          {
            displayText: t('suppliers'),
            props: {
              href: getRoute(`users/suppliers`),
            },
          },
          {
            displayText: t('newSupplier'),
            props: {
              color: 'secondary.main',
            },
          },
        ]}
      />

      {isPermissionAllow({ functionType: functionTypes.pageNew.id }) ? RenderForm() : <CommonRestricted />}
    </Box>
  );
}
